import { Splide } from '@hh/global.js'

export class FeaturesSlideshow extends HTMLElement {
  constructor() {
    super()
    this.slider = null
    this.slideCount = 0
    this.deviceBreakpoint = 767
    this.mobileSpeed = 800
    this.desktopSpeed = 400
    this.animationFrameId = null
    this.isAnimating = false
  }

  connectedCallback() {
    const slider = this.querySelector('[data-splide-slider]')
    this.slideCount = Number(this.dataset.slideCount || 0)
    if (slider) {
      this.initSlider(slider)
    }
  }

  disconnectedCallback() {
    // Cleanup animation frames and intervals
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId)
      this.animationFrameId = null
    }
    if (this.interval) {
      clearInterval(this.interval)
      this.interval = null
    }
    if (this.resizeTimout) {
      clearTimeout(this.resizeTimout)
      this.resizeTimout = null
    }
  }

  getSlideAnimationSpeed() {
    return document.documentElement.clientWidth < this.deviceBreakpoint
      ? this.mobileSpeed
      : this.desktopSpeed
  }

  setAnimatingState(animating) {
    this.isAnimating = animating
    const cursor = animating ? 'not-allowed' : ''
    this.style.cursor = cursor
    this.querySelectorAll('[data-splide-slide], [data-expand-button]').forEach(
      (el) => {
        el.style.cursor = cursor
      }
    )
  }

  async initSlider(slider) {
    this.slideAnimationSpeed = this.getSlideAnimationSpeed()

    this.slider = new Splide(slider, {
      drag: 'free',
      arrows: true,
      pagination: true,
      autoplay: false,
      useScroll: true,
      fixedWidth: true,
      gap: 24,
      padding: 16,
      snap: true,
      focus: this.slideCount >= 2 ? 'center' : 0,
      omitEnd: true,
      speed: this.slideAnimationSpeed,
      easing: 'linear',
      breakpoints: {
        [this.deviceBreakpoint]: {
          gap: 16,
          focus: 0,
        },
      },
    }).mount()

    if (document.documentElement.clientWidth < 768) {
      this.closeAllSlides()
    }

    this.precalculatePositions()

    this.slider.root
      .querySelectorAll('[data-expand-button]')
      .forEach((el) =>
        el.addEventListener('click', this.toggleExpandSlide.bind(this))
      )

    this.slider.root
      .querySelectorAll('[data-splide-slide]')
      .forEach((el) =>
        el.addEventListener('click', this.toggleExpandSlide.bind(this))
      )

    this.previousWindowWidth = window.innerWidth
    this.resizeTimout = null
    window.addEventListener('resize', () => {
      this.resizeTimout = setTimeout(() => {
        if (this.resizeTimout) {
          clearTimeout(this.resizeTimout)
        }

        if (this.previousWindowWidth !== window.innerWidth) {
          this.previousWindowWidth = window.innerWidth
          this.slideAnimationSpeed = this.getSlideAnimationSpeed()
          this.precalculatePositions()
        }
      }, 250)
    })
  }

  getTranslateXFromElement(elm) {
    if (!elm) {
      return 0
    }

    const style = window.getComputedStyle(elm)
    const transform = style.transform || 'none'

    if (transform === 'none') {
      return 0
    }
    const matrix = new DOMMatrix(transform)
    return matrix.m41
  }

  async precalculatePositions() {
    const sliderListComponent = this.slider.Components.Elements.list
    const slides = this.slider.Components.Elements.slides

    this.slider.options.speed = 0
    this.classList.add('precalculating-positions')

    for (const [index, slide] of slides.entries()) {
      this.slider.go(index)
      // Force reflow
      sliderListComponent.offsetHeight
      const positionClosed = this.getTranslateXFromElement(sliderListComponent)
      slide.setAttribute('data-precalc-position-closed', positionClosed)

      slide.classList.add('is-expanded')
      this.slider.go(index)
      // Force reflow
      sliderListComponent.offsetHeight
      this.slider.refresh()
      const positionOpened = this.getTranslateXFromElement(sliderListComponent)
      slide.setAttribute('data-precalc-position-open', positionOpened)
      slide.classList.remove('is-expanded')
    }

    // expand first slide on initial load if 3 slides or less
    if (this.slideCount <= 3 && slides.length > 0) {
      slides[0].classList.add('is-expanded')
    }

    this.classList.remove('precalculating-positions')
    this.slider.go(0)
    this.slider.options.speed = this.slideAnimationSpeed
  }

  closeAllSlides(animate = false) {
    this.querySelectorAll('[data-splide-slide]')?.forEach((slide) => {
      if (!slide.classList.contains('is-expanded')) return
      const heading = slide.querySelector('[data-slide-heading]')
      if (animate) {
        heading.style.minWidth = `${heading.offsetWidth}px`
        slide.classList.add('is-animating')
        clearInterval(this.interval)
        cancelAnimationFrame(this.animationFrameId)

        // Use requestAnimationFrame instead of setInterval for smoother updates
        const animateRefresh = () => {
          this.slider.refresh()
          this.animationFrameId = requestAnimationFrame(animateRefresh)
        }
        this.animationFrameId = requestAnimationFrame(animateRefresh)
      }
      slide.setAttribute('aria-expanded', 'false')
      slide.classList.remove('is-expanded')
      if (animate) {
        setTimeout(() => {
          if (this.animationFrameId) {
            cancelAnimationFrame(this.animationFrameId)
            this.animationFrameId = null
          }
          clearInterval(this.interval)
          heading.style.minWidth = null
          requestAnimationFrame(() => {
            this.slider.refresh()
            slide.classList.remove('is-animating')
          })
        }, 800)
      }
    })
  }

  toggleExpandSlide(event) {
    event.stopPropagation()

    // clicking during animation was causing undesired behaviour, so we're preventing it
    if (this.isAnimating) {
      return
    }

    const currentTarget = event.currentTarget

    const slide = currentTarget.closest('[data-splide-slide]')
    if (!slide) {
      return
    }
    const isExpanded = slide.classList.contains('is-expanded')

    this.setAnimatingState(true)

    const heading = slide.querySelector('[data-slide-heading]')

    // Use requestAnimationFrame for smoother initial setup
    requestAnimationFrame(() => {
      heading.style.minWidth = `${heading.offsetWidth}px`

      if (isExpanded) {
        slide.setAttribute('aria-expanded', 'false')
        currentTarget.setAttribute(
          'aria-label',
          currentTarget.dataset.labelReadMore
        )
      } else {
        this.closeAllSlides(true)
        slide.setAttribute('aria-expanded', 'true')
        currentTarget.setAttribute(
          'aria-label',
          currentTarget.dataset.labelReadLess
        )
      }
      slide.classList.toggle('is-expanded')
      slide.classList.add('is-animating')

      const newSlideIndex = Number(slide.dataset?.splideSlide || 0)
      const targetPosition = isExpanded
        ? Number(slide.dataset.precalcPositionClosed)
        : Number(slide.dataset.precalcPositionOpen)

      const shouldMoveDesktop =
        window.innerWidth >= this.deviceBreakpoint && this.slideCount >= 2

      if (shouldMoveDesktop && !isNaN(targetPosition)) {
        this.classList.add('custom-slide-move')
        this.style.setProperty('--target-position', `${targetPosition}px`)

        // Use requestAnimationFrame for smoother animation
        requestAnimationFrame(() => {
          setTimeout(() => {
            this.slider.go(newSlideIndex)
            requestAnimationFrame(() => {
              this.slider.refresh()
              // Use translate3d for hardware acceleration
              this.slider.Components.Elements.list.style.transform = `translate3d(${targetPosition}px, 0, 0)`

              setTimeout(() => {
                requestAnimationFrame(() => {
                  this.classList.remove('custom-slide-move')
                  slide.classList.remove('is-animating')
                  this.setAnimatingState(false)
                })
              }, this.slideAnimationSpeed)
            })
          }, this.slideAnimationSpeed * 1.5)
        })
      } else if (shouldMoveDesktop) {
        this.slider.go(newSlideIndex)
        setTimeout(() => {
          slide.classList.remove('is-animating')
          this.setAnimatingState(false)
        }, this.slideAnimationSpeed)
      } else {
        setTimeout(() => {
          slide.classList.remove('is-animating')
          this.setAnimatingState(false)
        }, this.slideAnimationSpeed)
      }

      setTimeout(() => {
        requestAnimationFrame(() => {
          heading.style.minWidth = null
        })
      }, this.slideAnimationSpeed)
    })
  }
}

customElements.define('features-slideshow', FeaturesSlideshow)